import {
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { MapMarker } from '@angular/google-maps';

import { IonFab, ToastController, ToastOptions } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { distance, point } from '@turf/turf';

import { compact, each, forEach, get, has, isNull, isUndefined, last, map, orderBy } from 'lodash-es';

import { defaultToastDuration } from '../../../../../../constants/toast.constants';
import { TooltipEnum } from '../../../../../../enums/tooltip';
import * as fromWalkingFarmActions from '../../../../../../store/actions/walking-farm.actions';
import { TooltipService } from '../../../../../services/tooltip.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-map-draw-walk-order',
  standalone: false,
  templateUrl: './map-draw-walk-order.component.html'
})
export class MapDrawWalkOrderComponent implements OnChanges {
  private store = inject(Store);
  private tooltipService = inject(TooltipService);
  private toastController = inject(ToastController);

  @ContentChildren(MapMarker, { descendants: true }) markers?: QueryList<MapMarker>;
  @ViewChild(IonFab, { read: ElementRef, static: true }) fabElement?: ElementRef;
  @Input() mapInstance: google.maps.Map | null = null;
  @Input() walkingFarmId: string | null = null;
  @Output() drawModeChange = new EventEmitter<boolean>();
  @Input() drawMode = false;

  mouseDownListener?: google.maps.MapsEventListener;
  mouseMoveListener?: google.maps.MapsEventListener;
  mouseUpListener?: google.maps.MapsEventListener;

  polyline: google.maps.Polyline | null = null;
  polylines: google.maps.Polyline[] = [];

  readonly polyLineOptions: google.maps.PolylineOptions = {
    clickable: false,
    geodesic: true,
    strokeColor: '#f4ee43',
    strokeOpacity: 1,
    strokeWeight: 10,
    zIndex: 1000
  };

  async ngOnChanges(changes: SimpleChanges) {
    if (has(changes, 'drawMode')) {
      await this.updateMapBehavior();
    }
  }

  async toggleDrawMode() {
    await this.tooltipService.dismissAll();
    this.drawMode = !this.drawMode;
    this.drawModeChange.emit(this.drawMode);
  }

  async updateMapBehavior() {
    if (this.mapInstance) {
      if (this.drawMode) {
        this.mapInstance.setOptions({
          gestureHandling: 'none'
        });
        this.mouseDownListener = this.mapInstance.addListener('mousedown', () => this.mapMouseDown());
        this.mouseUpListener = this.mapInstance.addListener('mouseup', () => this.mapMouseUp());
        this.markers?.forEach((mapMarker) => {
          mapMarker.marker?.setOpacity(0.7);
        });

        await this.tooltipService.show(
          {
            buttons: [
              {
                htmlAttributes: {
                  'aria-label': 'close'
                },
                icon: 'close'
              }
            ],
            color: 'light',
            message: 'Panning and Zooming are disabled during Draw Walk Order mode.',
            position: 'bottom',
            positionAnchor: 'bottom-anchor'
          },
          TooltipEnum.DrawMode
        );
      } else {
        this.mapInstance.setOptions({
          gestureHandling: 'auto'
        });
        this.mouseDownListener?.remove();
        this.mouseMoveListener?.remove();
        this.mouseUpListener?.remove();
        this.markers?.forEach((mapMarker) => {
          mapMarker.marker?.setOpacity(1);
        });
        this.clear();
      }
    }
  }

  mapMouseDown() {
    if (isNull(this.polyline)) {
      this.polyline = new google.maps.Polyline(this.polyLineOptions);
      this.polyline.setMap(this.mapInstance);
      this.mouseMoveListener = this.mapInstance?.addListener('mousemove', (event: google.maps.MapMouseEvent) =>
        this.mapMouseMove(event)
      );
    }
  }

  mapMouseMove(event: google.maps.MapMouseEvent) {
    if (!isNull(event.latLng)) {
      this.polyline?.getPath().push(event.latLng);
    }
  }

  async mapMouseUp() {
    if (!isNull(this.polyline)) {
      this.mouseMoveListener?.remove();
      this.polylines.push(this.polyline);
      this.polyline = null;
      await this.moveProperties();
    }
  }

  async moveProperties() {
    const lastPolyline = last(this.polylines);
    if (!isUndefined(lastPolyline)) {
      const linePoints = lastPolyline.getPath().getArray();
      let markers: MapMarker[] = [];
      let sortedMarkers: MapMarker[] = [];

      this.markers?.forEach((mapMarker) => {
        markers.push(mapMarker);
      });

      forEach(linePoints, (linePoint) => {
        const markersForSort: {
          distance: number;
          marker: MapMarker;
        }[] = [];
        const markersUnsorted: MapMarker[] = [];
        forEach(markers, (mapMarker) => {
          const position = mapMarker.marker?.getPosition();
          if (!isUndefined(position) && !isNull(position)) {
            const from = point([linePoint.lng(), linePoint.lat()]);
            const markerPosition = new google.maps.LatLng(position);
            const to = point([markerPosition.lng(), markerPosition.lat()]);
            const dist = distance(from, to, { units: 'meters' });
            if (dist < 15) {
              markersForSort.push({
                distance: dist,
                marker: mapMarker
              });
            } else {
              markersUnsorted.push(mapMarker);
            }
          }
        });
        sortedMarkers = [...sortedMarkers, ...map(orderBy(markersForSort, ['distance'], ['asc']), 'marker')];
        markers = markersUnsorted;
      });

      if (this.walkingFarmId) {
        const toastOptions: ToastOptions = {
          color: 'light',
          duration: defaultToastDuration,
          message: 'There are no farm properties in your selection. Please try again.',
          position: 'bottom',
          positionAnchor: 'bottom-anchor'
        };

        if (sortedMarkers.length > 0) {
          const order = map(
            compact(
              map(
                [...sortedMarkers, ...markers],
                (item): string | number | null => get(item, 'marker.propertyId') ?? null
              )
            ),
            (item) => `${item}`
          );

          toastOptions.message = 'Selected properties are now sorted and moved to the top of the list.';

          this.store.dispatch(
            fromWalkingFarmActions.updatePropertiesOrder({
              payload: {
                farmId: this.walkingFarmId,
                order
              }
            })
          );
        }

        await this.tooltipService.dismissAll();
        const toast = await this.toastController.create({
          ...toastOptions
        });
        await toast?.present();
      }
    }
  }

  clear() {
    each(this.polylines, (polyline) => {
      polyline.setMap(null);
    });
    if (this.polyline) {
      this.polyline?.setMap(null);
      this.polyline = null;
    }
    this.polylines = [];
  }
}
